/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { useCallback, useMemo, useState } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import {
  SOCIAL_PROVIDERS,
  SocialAuthBody,
  SocialAuthResponse,
  SocialProvider,
} from '../managers';
import { describeRestoreError } from '../utils/accountRecovery';
import { describeSocialAuthError } from '../utils/ssoErrors';
import {
  isAppleAuthCancelled,
  resolveAppleAuthFailure,
} from '../utils/appleAuthErrors';
import { isIncompleteSocialAuth } from '../utils/googleAuthExchange';
import {
  isSocialAuthCancelled,
  validateAppleLoginData,
  validateFacebookLoginData,
  validateGoogleLoginData,
} from '../utils/socialSignIn';
import type {
  LoginData,
  LoginError,
  SocialLoginResult,
  LoginNotification,
  onAppleLoginRequest,
  onFacebookLoginRequest,
  onGoogleLoginRequest,
} from '../widgets/LoginView/types';
import {
  useLoginByFacebook,
  useLoginByGoogle,
  useSSOAppleLogin,
} from './account';
import type { KeepAccountRecoveryHandlers } from './useKeepAccountRecovery';
import { useSocialLoginWeb } from './useSocialLoginWeb';

import { describeError } from '../utils/describeError';
export interface UseSocialLoginFlowOptions {
  authType?: SocialAuthBody['authType'];
  onGoogleLoginRequest?: onGoogleLoginRequest;
  onFacebookLoginRequest?: onFacebookLoginRequest;
  onAppleLoginRequest?: onAppleLoginRequest;
  /** Awaited: it runs the sign-in tail and navigates, so the loader spans it. */
  onLoginSuccess?: (result: SocialLoginResult) => void | Promise<void>;
  onLoginError?: (errorData: LoginError) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  setNotification: (notification: LoginNotification | null) => void;
  accountRecovery?: KeepAccountRecoveryHandlers;
}

export function useSocialLoginFlow(options: UseSocialLoginFlowOptions) {
  const {
    authType = 'login',
    onGoogleLoginRequest,
    onFacebookLoginRequest,
    onAppleLoginRequest,
    onLoginSuccess,
    onLoginError,
    onLoadingChange,
    setNotification,
    accountRecovery,
  } = options;

  const { t } = useTranslation('account');
  const isSignup = authType === 'signup';
  const reportError = useSsoErrorReporter(
    isSignup ? SCREEN_NAME.signupV2 : SCREEN_NAME.loginV2,
  );

  /**
   * True from the moment a provider is tapped until the sign-in has navigated.
   * Cleared only on failure: a success unmounts this screen.
   */
  const [isSocialLoginPending, setLoading] = useState(false);

  const setPending = useCallback(
    (pending: boolean) => {
      setLoading(pending);
      onLoadingChange?.(pending);
    },
    [onLoadingChange],
  );

  const { mutateAsync: googleLoginMutate } = useLoginByGoogle();
  const { mutateAsync: facebookLoginMutate } = useLoginByFacebook();
  const { mutateAsync: appleLoginMutate } = useSSOAppleLogin();

  const googleLogin = useSocialLoginWeb(SOCIAL_PROVIDERS.GOOGLE, authType);
  const facebookLogin = useSocialLoginWeb(SOCIAL_PROVIDERS.FACEBOOK, authType);
  const appleLogin = useSocialLoginWeb(SOCIAL_PROVIDERS.APPLE, authType);
  const weChatLogin = useSocialLoginWeb(SOCIAL_PROVIDERS.WECHAT, authType);

  const isWeb = Platform.OS === 'web';

  const showError = useCallback(
    (message: string) => {
      setNotification({ message, type: NotificationViewStyle.ERROR });
    },
    [setNotification],
  );

  const showRestoreFailure = useCallback(
    (error: unknown, fallbackKey: string) => {
      const message = describeRestoreError(error, t, fallbackKey);
      reportError(error, message);
      showError(message);
    },
    [reportError, showError, t],
  );

  const mapApiError = useCallback(
    (error: unknown, fallback: string) =>
      describeSocialAuthError(error, t, fallback).message,
    [t],
  );

  const notifySuccess = useCallback(
    async (data: LoginData, method?: SocialProvider) => {
      await onLoginSuccess?.({
        kind: 'authenticated',
        data: method ? ({ ...data, method } as LoginData) : data,
      });
    },
    [onLoginSuccess],
  );

  // Show an error if sign-in fails after the social login succeeded.
  const showSignInFailure = useCallback(
    (error: unknown) =>
      showError(
        mapApiError(
          error,
          isSignup ? t('signUpView.signUpFailed') : t('socialAuthErrors.oops'),
        ),
      ),
    [isSignup, mapApiError, showError, t],
  );

  const handleLoginWeb = useCallback(
    (provider: SocialProvider) => {
      const loginMap: Record<
        SocialProvider,
        ReturnType<typeof useSocialLoginWeb>
      > = {
        google: googleLogin,
        facebook: facebookLogin,
        apple: appleLogin,
        wechat: weChatLogin,
      };

      const loginFn = loginMap[provider].mutate;

      setPending(true);

      loginFn(undefined, {
        onSuccess: (data: SocialAuthResponse) => {
          setNotification(null); // hide notification
          void notifySuccess(data, provider)
            .catch(showSignInFailure)
            .finally(() => {
              setPending(false);
            });
        },
        onError: (error) => {
          setPending(false);
          if (isSocialAuthCancelled(error)) {
            if (!isSignup) {
              showError(
                error instanceof Error
                  ? error.message
                  : t('loginView.loginFailed'),
              );
            }
            return;
          }
          if (
            accountRecovery?.tryHandleScheduledDeletion(error, () => {
              setPending(true);
              loginFn(
                { restoreAccount: true },
                {
                  onSuccess: (data: SocialAuthResponse) => {
                    setNotification(null);
                    void notifySuccess(data, provider)
                      .catch(showSignInFailure)
                      .finally(() => {
                        setPending(false);
                      });
                  },
                  onError: (retryError) => {
                    setPending(false);
                    showRestoreFailure(retryError, 'loginView.loginFailed');
                  },
                },
              );
            })
          ) {
            setNotification(null);
            return;
          }
          const message = mapApiError(
            error,
            isSignup
              ? t('signUpView.signUpFailed')
              : t('socialAuthErrors.oops'),
          );
          reportError(error, message);
          showError(message);
        },
        onSettled: () => {
          if (!isSignup) {
            setPending(false);
          }
        },
      });
    },
    [
      googleLogin,
      facebookLogin,
      appleLogin,
      weChatLogin,
      isSignup,
      mapApiError,
      notifySuccess,
      reportError,
      setPending,
      setNotification,
      accountRecovery,
      showError,
      showSignInFailure,
      showRestoreFailure,
      t,
    ],
  );

  const runAppleExchange = useCallback(
    async (
      appleData: Awaited<ReturnType<onAppleLoginRequest>>,
      restoreAccount?: boolean,
    ) => {
      await appleLoginMutate({
        appleId: appleData.user.id,
        authCode: appleData.authCode || '',
        email: appleData.user.email || undefined,
        firstName: appleData.user.firstName || undefined,
        lastName: appleData.user.lastName || undefined,
        ...(restoreAccount ? { restoreAccount } : {}),
      });
    },
    [appleLoginMutate],
  );

  // The native Apple Sign-In module is only available on iOS, not Android.
  const handleAppleLogin = useCallback(async () => {
    if (Platform.OS !== 'ios') {
      const errorMessage = isSignup
        ? t('signUpView.appleSignUpNotConfigured')
        : t('loginView.appleSignInavailable');
      console.error('Apple Sign-In error:', errorMessage);
      onLoginError?.({ method: 'apple', error: errorMessage });
      return;
    }

    if (!onAppleLoginRequest) {
      const errorMessage = isSignup
        ? t('signUpView.appleSignUpNotConfigured')
        : t('loginView.appleSignInNotConfigured');
      onLoginError?.({ method: 'apple', error: errorMessage });
      return;
    }

    let firstAppleData: Awaited<ReturnType<onAppleLoginRequest>> | undefined;
    try {
      setPending(true);
      setNotification(null);

      const appleData = await onAppleLoginRequest();
      firstAppleData = appleData;

      const validation = validateAppleLoginData(appleData);
      if (!validation.isValid) {
        throw new Error(validation.error || t('loginView.appleDataInvalid'));
      }

      await runAppleExchange(appleData);

      await notifySuccess(appleData, SOCIAL_PROVIDERS.APPLE);
    } catch (error) {
      if (isAppleAuthCancelled(error)) {
        return;
      }

      const failure = resolveAppleAuthFailure(
        error,
        t,
        t('appleAuthErrors.genericError'),
      );

      if (failure.kind === 'cancelled') {
        return;
      }

      const errorMessage = failure.message;
      console.error('Apple Sign-In error:', errorMessage, describeError(error));
      reportError(error, errorMessage);
      onLoginError?.({ method: 'apple', error: errorMessage });

      if (
        accountRecovery?.tryHandleScheduledDeletion(error, () => {
          void (async () => {
            setPending(true);
            try {
              // Apple auth codes are single-use, so the restore needs a fresh
              // one; email/name only arrive on the first authorization.
              const retryData = await onAppleLoginRequest();
              const firstUser = firstAppleData?.user;
              const appleData = {
                ...retryData,
                user: {
                  ...retryData.user,
                  email: retryData.user.email || firstUser?.email || null,
                  firstName:
                    retryData.user.firstName || firstUser?.firstName || null,
                  lastName:
                    retryData.user.lastName || firstUser?.lastName || null,
                },
              };
              const validation = validateAppleLoginData(appleData);
              if (!validation.isValid) {
                throw new Error(
                  validation.error || t('loginView.appleDataInvalid'),
                );
              }
              await runAppleExchange(appleData, true);
              await notifySuccess(appleData, SOCIAL_PROVIDERS.APPLE);
            } catch (retryError) {
              if (isAppleAuthCancelled(retryError)) {
                return;
              }
              showRestoreFailure(retryError, 'loginView.appleSignInFailed');
            } finally {
              setPending(false);
            }
          })();
        })
      ) {
        setNotification(null);
        return;
      }

      showError(errorMessage);
    } finally {
      setPending(false);
    }
  }, [
    isSignup,
    onAppleLoginRequest,
    runAppleExchange,
    notifySuccess,
    onLoginError,
    reportError,
    setPending,
    setNotification,
    accountRecovery,
    showError,
    showRestoreFailure,
    t,
  ]);

  const handleGoogleLogin = useCallback(async () => {
    if (!onGoogleLoginRequest) {
      const errorMessage = isSignup
        ? t('signUpView.googleSignUpNotConfigured')
        : t('loginPage.googleSignInNotConfigured');
      onLoginError?.({ method: 'google', error: errorMessage });
      return;
    }

    const exchangeGoogle = async (
      googleData: Awaited<ReturnType<onGoogleLoginRequest>>,
      restoreAccount?: boolean,
    ) => {
      const exchange = await googleLoginMutate({
        googleAuthToken: googleData.idToken || '',
        googleClientId: googleData.googleClientId || '',
        optIn2FA: false,
        ...(restoreAccount ? { restoreAccount } : {}),
      });

      if (isIncompleteSocialAuth(exchange)) {
        await onLoginSuccess?.({
          kind: 'incomplete',
          data: {
            ...exchange,
            // Fallback to Google profile email when 202 omits it.
            email: exchange.email ?? googleData.user?.email ?? undefined,
          },
        });
        return;
      }

      await notifySuccess(googleData, SOCIAL_PROVIDERS.GOOGLE);
    };

    // Set once the Google token passes validation; the keep-account retry
    // reuses it instead of asking the user to sign in again.
    let googleDataForRetry:
      | Awaited<ReturnType<onGoogleLoginRequest>>
      | undefined;
    try {
      setPending(true);

      const googleData = await onGoogleLoginRequest();

      const validation = validateGoogleLoginData(googleData);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
      googleDataForRetry = googleData;

      await exchangeGoogle(googleData);
    } catch (error) {
      if (isSocialAuthCancelled(error)) {
        if (!isSignup) {
          showError(
            error instanceof Error
              ? error.message
              : t('loginPage.googleSignInFailed'),
          );
        }
        return;
      }

      const errorMessage = mapApiError(error, t('socialAuthErrors.oops'));
      reportError(error, errorMessage);
      onLoginError?.({ method: 'google', error: errorMessage });

      const retryGoogleData = googleDataForRetry;
      if (
        retryGoogleData &&
        accountRecovery?.tryHandleScheduledDeletion(error, () => {
          void (async () => {
            setPending(true);
            try {
              await exchangeGoogle(retryGoogleData, true);
            } catch (retryError) {
              showRestoreFailure(retryError, 'loginPage.googleSignInFailed');
            } finally {
              setPending(false);
            }
          })();
        })
      ) {
        setNotification(null);
        return;
      }

      showError(errorMessage);
    } finally {
      setPending(false);
    }
  }, [
    isSignup,
    onGoogleLoginRequest,
    googleLoginMutate,
    mapApiError,
    notifySuccess,
    onLoginSuccess,
    onLoginError,
    reportError,
    setPending,
    setNotification,
    accountRecovery,
    showError,
    showRestoreFailure,
    t,
  ]);

  const handleFacebookLogin = useCallback(async () => {
    if (!onFacebookLoginRequest) {
      const errorMessage = t('loginView.facebookSignInNotConfigured');
      onLoginError?.({ method: 'facebook', error: errorMessage });
      return;
    }

    // Set once the Facebook token passes validation; the keep-account retry
    // reuses it instead of asking the user to sign in again.
    let facebookDataForRetry:
      | Awaited<ReturnType<onFacebookLoginRequest>>
      | undefined;
    try {
      setPending(true);

      const facebookData = await onFacebookLoginRequest();

      const validation = validateFacebookLoginData(facebookData);
      if (!validation.isValid) {
        throw new Error(
          validation.error ||
            'Invalid Facebook Sign-In data received from native app',
        );
      }
      facebookDataForRetry = facebookData;

      await facebookLoginMutate({
        facebookAuthToken: facebookData.accessToken || '',
        optIn2FA: false,
        status: 'active',
      });

      // Navigated; this screen unmounts, so the loader is left up on purpose.
      await notifySuccess(facebookData, SOCIAL_PROVIDERS.FACEBOOK);
    } catch (error) {
      const errorMessage = mapApiError(error, t('socialAuthErrors.oops'));
      console.error(
        'Facebook Sign-In error:',
        errorMessage,
        describeError(error),
      );
      reportError(error, errorMessage);
      setPending(false);
      onLoginError?.({ method: 'facebook', error: errorMessage });

      const retryFacebookData = facebookDataForRetry;
      if (
        retryFacebookData &&
        accountRecovery?.tryHandleScheduledDeletion(error, () => {
          void (async () => {
            setPending(true);
            try {
              await facebookLoginMutate({
                facebookAuthToken: retryFacebookData.accessToken || '',
                optIn2FA: false,
                status: 'active',
                restoreAccount: true,
              });
              await notifySuccess(retryFacebookData, SOCIAL_PROVIDERS.FACEBOOK);
            } catch (retryError) {
              showRestoreFailure(retryError, 'loginView.facebookSignInFailed');
            } finally {
              setPending(false);
            }
          })();
        })
      ) {
        setNotification(null);
        return;
      }

      showError(errorMessage);
    }
  }, [
    onFacebookLoginRequest,
    facebookLoginMutate,
    mapApiError,
    notifySuccess,
    onLoginError,
    reportError,
    setPending,
    setNotification,
    accountRecovery,
    showError,
    showRestoreFailure,
    t,
  ]);

  const socialLoginHandlers: Record<SocialProvider, () => void> = useMemo(
    () => ({
      facebook: () =>
        isWeb
          ? handleLoginWeb(SOCIAL_PROVIDERS.FACEBOOK)
          : handleFacebookLogin(),
      google: () =>
        isWeb ? handleLoginWeb(SOCIAL_PROVIDERS.GOOGLE) : handleGoogleLogin(),
      apple: () =>
        isWeb ? handleLoginWeb(SOCIAL_PROVIDERS.APPLE) : handleAppleLogin(),
      wechat: () =>
        isWeb
          ? handleLoginWeb(SOCIAL_PROVIDERS.WECHAT)
          : console.warn('WeChat login not implemented'),
    }),
    [
      isWeb,
      handleLoginWeb,
      handleFacebookLogin,
      handleGoogleLogin,
      handleAppleLogin,
    ],
  );

  return { socialLoginHandlers, isSocialLoginPending };
}
